import { useMemo, useState, useCallback, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { useControlCenter } from "../../AppShell"
import { useSubscription,
  useCancelSubscription,
  useNotificationPreferences,
  useSubscriptionCheckout,
} from "../../hooks/useApi"
import {
  useThemeSurfaces,
  type ButtonTone,
  type PanelTone,
  type PanelInset,
  type OverlayTokens,
  type ThemeSurfaces,
} from "../../theme/useThemeSurfaces"

import { rememberLastContact } from "../../constants/storage"
import type { SubscriptionRecord, SubscriptionNotificationEvent } from "../../types"

type CheckoutFormState = {
  contactId: string
  amount: string
  cadence: string
  currency: string
  description: string
  email: string
}

type ContactOption = {
  id: string
  label: string
}

export default function BillingPage() {
  const surfaces = useThemeSurfaces()
  const panelTokens = surfaces.panel
  const buttonTokens = surfaces.button
  const overlayTokens = surfaces.overlay
  const inputTokens = surfaces.input
  const highlightTokens = surfaces.highlight
  const borderTokens = surfaces.border
  const cardStyles = panelTokens.primary
  const insetPanel = panelTokens.inset
  const tertiaryPanel = panelTokens.tertiary
  const primaryButton = buttonTokens.solid
  const outlineButton = buttonTokens.outline
  const headingColor = cardStyles.heading
  const mutedColor = cardStyles.muted
  const cancel = useCancelSubscription()
  const notificationPrefs = useNotificationPreferences()
  const checkout = useSubscriptionCheckout()
  const navigate = useNavigate()
  const { tenant } = useControlCenter()
  const { data, isLoading, error, refetch } = useSubscription()

  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [checkoutForm, setCheckoutForm] = useState<CheckoutFormState>({
    contactId: "",
    amount: "",
    cadence: "month",
    currency: "USD",
    description: "Monthly plan",
    email: "",
  })
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const plan = data?.plan
  const planDefaults = plan?.defaults ?? {}
  const planName = plan?.name ?? "Monthly plan"
  const planPriceCents = plan?.price_cents ?? null
  const planInterval = plan?.interval ?? null
  const defaultCurrency = plan?.currency ?? planDefaults.currency ?? "USD"
  const defaultCadence = planInterval ?? planDefaults.cadence ?? "month"
  const defaultAmountCents = planPriceCents ?? planDefaults.amount_cents ?? 4900
  const subscriptionStatus = data?.status ?? "none"
  const invoicesLast30 = data?.usage?.invoices_last_30_days ?? 0
  const cards = data?.cards ?? []
  const records = useMemo(
    () => ((data?.records ?? []) as SubscriptionRecord[]),
    [data?.records],
  )
  const notifications = useMemo(
    () => ((data?.notifications ?? []) as SubscriptionNotificationEvent[]),
    [data?.notifications],
  )
  const contactEmailLookup = useMemo(() => {
    const map: Record<string, string> = {}
    for (const record of records) {
      const contactId = record.contact_id
      if (!contactId) continue
      const metadataEmail = typeof record.metadata?.email === "string" ? (record.metadata.email as string) : null
      if (metadataEmail) {
        map[contactId] = metadataEmail
      }
    }
    return map
  }, [records])
  const channelPills = notificationPrefs.data?.channels ?? {}
  const checkoutSessionUrl =
    data?.checkout_session?.url ?? cards.find((card) => card.id === "plan")?.checkout_url ?? null
  const portalUrl = data?.portal_url ?? null
  const primaryContactId = useMemo(() => {
    if (data?.primary_contact_id) {
      return data.primary_contact_id
    }
    const active = records.find((record) => record.status !== "canceled")
    return active?.contact_id ?? records[0]?.contact_id ?? ""
  }, [data?.primary_contact_id, records])
  const contactOptions = useMemo(() => buildContactOptions(primaryContactId, records), [primaryContactId, records])
  const cadenceOptions = useMemo(() => ["month", "quarter", "year"], [])
  const pendingRecords = useMemo(
    () => records.filter((record) => (record.status ?? "").toLowerCase() === "pending"),
    [records],
  )
  const activeRecords = useMemo(
    () => records.filter((record) => (record.status ?? "").toLowerCase() !== "pending"),
    [records],
  )

  const handleCheckoutFieldChange = useCallback((field: keyof CheckoutFormState, value: string) => {
    setCheckoutForm((previous) => ({ ...previous, [field]: value }))
  }, [])

  const openCheckout = useCallback(
    (seed?: {
      contactId?: string | null
      amountCents?: number | null
      cadence?: string | null
      description?: string | null
      email?: string | null
    }) => {
      const amountCents = seed?.amountCents ?? defaultAmountCents ?? 0
      const seedContactId = seed?.contactId ?? primaryContactId ?? ""
      const seedEmail = seed?.email ?? (seedContactId ? contactEmailLookup[seedContactId] ?? "" : "")
      setCheckoutForm({
        contactId: seedContactId,
        amount: centsToDisplay(amountCents),
        cadence: seed?.cadence ?? defaultCadence,
        currency: defaultCurrency,
        description: seed?.description ?? planName,
        email: seedEmail,
      })
      setCheckoutError(null)
      setCheckoutOpen(true)
    },
    [defaultAmountCents, defaultCadence, defaultCurrency, planName, primaryContactId, contactEmailLookup],
  )

  const closeCheckout = useCallback(() => {
    if (checkout.isPending) return
    setCheckoutOpen(false)
    setCheckoutError(null)
  }, [checkout.isPending])

  const handleCheckoutSubmit = useCallback(async () => {
    const contactId = checkoutForm.contactId.trim()
    if (!contactId) {
      setCheckoutError("Contact required")
      return
    }
    const amountValue = Number.parseFloat(checkoutForm.amount)
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      setCheckoutError("Amount must be positive")
      return
    }
    const amountCents = Math.round(amountValue * 100)
    if (amountCents <= 0) {
      setCheckoutError("Amount must be positive")
      return
    }
    const emailFromLookup = contactEmailLookup[contactId]
    const emailValue = checkoutForm.email.trim()
    const resolvedEmail = emailValue || emailFromLookup || ""
    if (!resolvedEmail) {
      setCheckoutError("Contact email required for checkout")
      return
    }
    try {
      setCheckoutError(null)
      const response = await checkout.mutateAsync({
        contactId,
        amountCents,
        cadence: checkoutForm.cadence || defaultCadence,
        currency: checkoutForm.currency || defaultCurrency,
        description: checkoutForm.description.trim() || planName,
        email: resolvedEmail,
      })
      const url = response.checkout_session?.url
      if (url) {
        window.open(url, "_blank", "noopener")
      }
      setCheckoutOpen(false)
      await refetch()
    } catch (err) {
      if (err instanceof Error) {
        setCheckoutError(err.message)
      } else {
        setCheckoutError("Unable to create checkout session. Try again soon.")
      }
    }
  }, [checkout, checkoutForm, contactEmailLookup, defaultCadence, defaultCurrency, planName, refetch])

  const handleManagePlan = useCallback(() => {
    if (portalUrl && subscriptionStatus !== "pending") {
      window.open(portalUrl, "_blank", "noopener")
    }
  }, [portalUrl, subscriptionStatus])

  const handleResumeSubscription = useCallback(async () => {
    try {
      await cancel.mutateAsync(false)
      refetch()
    } catch (err) {
      console.error("Failed to resume subscription", err)
    }
  }, [cancel, refetch])

  const handleCancelSubscription = useCallback(async () => {
    try {
      await cancel.mutateAsync(true)
      refetch()
    } catch (err) {
      console.error("Failed to cancel subscription", err)
    }
  }, [cancel, refetch])

  const handleOpenChat = useCallback(
    (contactId?: string | null) => {
      const target = contactId && contactId.trim() ? contactId : primaryContactId
      if (target && typeof window !== "undefined") {
        rememberLastContact(target)
        navigate(`/app/customers?contactId=${encodeURIComponent(target)}`)
        return
      }
      navigate("/app/customers")
    },
    [navigate, primaryContactId],
  )

  const handleManageNotifications = useCallback(() => {
    navigate("/app/settings#notifications")
  }, [navigate])

  const handleStartSubscription = useCallback(() => {
    openCheckout()
  }, [openCheckout])

  const handleChangeCadence = useCallback(() => {
    openCheckout({
      contactId: primaryContactId,
      amountCents: planPriceCents ?? defaultAmountCents,
      cadence: planInterval ?? defaultCadence,
      description: planName,
    })
  }, [openCheckout, primaryContactId, planPriceCents, defaultAmountCents, planInterval, defaultCadence, planName])

  const handleResumeCheckout = useCallback(() => {
    if (checkoutSessionUrl) {
      window.open(checkoutSessionUrl, "_blank", "noopener")
    }
  }, [checkoutSessionUrl])

  const handleRecordChat = useCallback(
    (record: SubscriptionRecord) => {
      handleOpenChat(record.contact_id)
    },
    [handleOpenChat],
  )

  const handleRecordAdjust = useCallback(
    (record: SubscriptionRecord) => {
      openCheckout({
        contactId: record.contact_id ?? "",
        amountCents: record.amount_cents ?? defaultAmountCents,
        cadence: record.cadence ?? defaultCadence,
        description: record.description ?? planName,
        email: typeof record.metadata?.email === "string" ? (record.metadata.email as string) : null,
      })
    },
    [openCheckout, defaultAmountCents, defaultCadence, planName],
  )

  const handleRecordCheckoutResume = useCallback((record: SubscriptionRecord) => {
    if (record.checkout_url) {
      window.open(record.checkout_url, "_blank", "noopener")
    }
  }, [])

  const manageDisabled = !portalUrl || subscriptionStatus === "pending"
  const cancelDisabled = cancel.isPending || subscriptionStatus === "pending"

  // Check payments_ready first - no need to wait for API if Stripe isn't connected
  if (!tenant.payments_ready) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "clamp(16px, 4vw, 24px)" }}>
        <section
          style={{
            background: "rgba(255,43,79,0.95)",
            border: "2px solid #0a0a0b",
            padding: "clamp(24px, 5vw, 36px)",
            display: "grid",
            gap: "16px",
            boxShadow: "0 0 32px rgba(255,43,79,0.35)",
          }}
        >
          <header className="mono" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "#0a0a0b" }}>
            Create products with Stripe
          </header>
          <p className="small" style={{ textTransform: "none", color: "#0a0a0b", lineHeight: 1.6 }}>
            Before you can create products for your customers to purchase or subscriptions they can subscribe to,
            you need to connect your Stripe account. This enables secure payment processing and automatic billing.
          </p>
          <button
            type="button"
            onClick={() => navigate("/app/dashboard")}
            className="mono"
            style={{
              border: "2px solid #0a0a0b",
              padding: "14px 20px",
              background: "#0a0a0b",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: "pointer",
              color: "#fff",
              minHeight: "48px",
              justifySelf: "flex-start",
            }}
          >
            Connect Stripe
          </button>
        </section>
      </div>
    )
  }

  // Only show loading when Stripe IS connected and we're fetching products
  if (isLoading) {
    return <StatusPanel panel={cardStyles}>Loading products…</StatusPanel>
  }

  if (error) {
    return (
      <StatusPanel panel={cardStyles}>
        <div style={{ display: "grid", gap: "12px", textAlign: "center" }}>
          <span>We could not retrieve product data. Try again shortly.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="mono"
            style={{
              ...outlineButtonStyles(outlineButton),
              justifySelf: "center",
            }}
          >
            Retry
          </button>
        </div>
      </StatusPanel>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "clamp(16px, 4vw, 24px)" }}>
      <section
        data-testid="billing-products-section"
        style={{
          background: cardStyles.background,
          border: cardStyles.border,
          boxShadow: cardStyles.shadow,
          padding: "clamp(20px, 4vw, 28px)",
          display: "grid",
          gap: "clamp(12px, 3vw, 18px)",
        }}
      >
        <header className="mono" style={{ letterSpacing: "0.18em", textTransform: "uppercase" as const, color: cardStyles.heading }}>
          Products
        </header>
        {cards.length ? (
          <div style={{ display: "grid", gap: "12px" }}>
            <article
              aria-label="Create subscription"
              style={{
                border: insetPanel.border,
                padding: "16px",
                display: "grid",
                gap: "8px",
                background: insetPanel.background,
                boxShadow: panelTokens.secondary.shadow,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                <span className="mono" style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>Create subscription</span>
                <span className="mono tiny" style={{ letterSpacing: "0.1em", color: mutedColor }}>Wizard</span>
              </div>
              <p className="small" style={{ textTransform: "none", color: cardStyles.muted }}>
                Launch a new checkout session with custom cadence, amount, and contact details. Checkout requires a billing email.
              </p>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button type="button" onClick={handleStartSubscription} className="mono" style={outlineButtonStyles(outlineButton)}>
                  Create subscription
                </button>
                {subscriptionStatus === "pending" && checkoutSessionUrl ? (
                  <button type="button" onClick={handleResumeCheckout} className="mono" style={outlineButtonStyles(outlineButton)}>
                    Resume latest checkout
                  </button>
                ) : null}
              </div>
            </article>
            {cards.map((card) => (
              <article
                key={card.id}
                style={{
                border: tertiaryPanel.border,
                padding: "16px",
                display: "grid",
                gap: "8px",
                background: tertiaryPanel.background,
                boxShadow: tertiaryPanel.shadow,

                }}
                role="group"
                aria-labelledby={`subscription-${card.id}`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                  <span className="mono" id={`subscription-${card.id}`} style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    {card.title}
                  </span>
                  <span className="mono small" style={{ letterSpacing: "0.1em", color: resolveStatusColor(card.status) }}>
                    {card.status.toUpperCase()}
                  </span>
                </div>

                {card.id === "plan" ? (
                  <div style={{ display: "grid", gap: "6px" }}>
                    <div style={{ fontSize: "24px", fontWeight: 600 }}>{plan?.name ?? card.title}</div>
                    <div className="mono" style={{ color: cardStyles.muted, letterSpacing: "0.08em" }}>
                      {formatPrice(card.amount_cents ?? plan?.price_cents ?? null, card.currency ?? plan?.currency ?? defaultCurrency)} · billed per {card.interval ?? plan?.interval ?? defaultCadence}
                    </div>
                    <div className="small" style={{ textTransform: "none" as const, color: cardStyles.muted }}>
                      Next renewal {card.current_period_end ? new Date(card.current_period_end).toLocaleDateString() : plan?.current_period_end ? new Date(plan.current_period_end).toLocaleDateString() : "—"}
                    </div>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={handleManagePlan}
                        disabled={manageDisabled}
                        className="mono"
                        style={primaryButtonStyles(primaryButton, manageDisabled)}
                      >
                        Manage plan
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenChat(primaryContactId)}
                        className="mono"
                        style={outlineButtonStyles(outlineButton)}
                      >
                        Message concierge
                      </button>
                      <button type="button" onClick={handleStartSubscription} className="mono" style={outlineButtonStyles(outlineButton)}>
                        Create subscription
                      </button>
                      {subscriptionStatus === "pending" && checkoutSessionUrl ? (
                        <button type="button" onClick={handleResumeCheckout} className="mono" style={outlineButtonStyles(outlineButton)}>
                          Complete checkout
                        </button>
                      ) : null}
                      {subscriptionStatus !== "pending" ? (
                        <button type="button" onClick={handleChangeCadence} className="mono" style={outlineButtonStyles(outlineButton)}>
                          Change cadence
                        </button>
                      ) : null}
                      {card.cancel_at_period_end ? (
                        <button type="button" onClick={handleResumeSubscription} className="mono" style={outlineButtonStyles(outlineButton)} disabled={cancelDisabled}>
                          Resume renewal
                        </button>
                      ) : (
                        <button type="button" onClick={handleCancelSubscription} className="mono" style={outlineButtonStyles(outlineButton)} disabled={cancelDisabled}>
                          Cancel at period end
                        </button>
                      )}
                    </div>
                    {records.length ? (
                      <div
                        data-testid="billing-contact-subscriptions"
                        style={{ display: "grid", gap: "14px", paddingTop: "12px", borderTop: borderTokens.divider }}
                      >
                        <header className="mono" style={{ letterSpacing: "0.12em", textTransform: "uppercase", color: cardStyles.heading }}>
                          Contact subscriptions
                        </header>
                        {pendingRecords.length ? (
                          <SubscriptionRecordSection
                            title="Pending checkouts"
                            records={pendingRecords}
                            defaultCurrency={defaultCurrency}
                            defaultCadence={defaultCadence}
                            onOpenChat={handleRecordChat}
                            onResumeCheckout={handleRecordCheckoutResume}
                            onAdjust={handleRecordAdjust}
                          />
                        ) : null}
                        {activeRecords.length ? (
                          <SubscriptionRecordSection
                            title="Active subscriptions"
                            records={activeRecords}
                            defaultCurrency={defaultCurrency}
                            defaultCadence={defaultCadence}
                            onOpenChat={handleRecordChat}
                            onResumeCheckout={handleRecordCheckoutResume}
                            onAdjust={handleRecordAdjust}
                          />
                        ) : null}
                        {!pendingRecords.length && !activeRecords.length ? (
                          <span className="small" style={{ textTransform: "none", color: cardStyles.muted }}>
                            No subscription records available yet.
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {card.id === "weekly_digest" ? (
                  <div style={{ display: "grid", gap: "8px" }}>
                    <div className="small" style={{ textTransform: "none" as const, color: cardStyles.muted }}>
                      Keep weekly digest emails {card.status === "enabled" ? "flowing to your inbox." : "off until you re-enable them."}
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {Object.entries(channelPills).map(([key, value]) => (
                        <span
                          key={key}
                          className="tiny mono"
                          style={{
                            padding: "4px 10px",
                            border: value?.digest_enabled ? "1px solid var(--rail)" : insetPanel.border,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            background: value?.digest_enabled ? highlightTokens.soft : "transparent",
                            color: value?.digest_enabled ? "var(--rail)" : mutedColor,
                          }}
                        >
                          {key.toUpperCase()}
                        </span>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                      <button type="button" className="mono" onClick={handleManageNotifications} style={outlineButtonStyles(outlineButton)}>
                        Manage notifications
                      </button>
                      {card.updated_at ? (
                        <span className="mono small" style={{ color: cardStyles.muted, letterSpacing: "0.08em" }}>
                          Updated {new Date(card.updated_at).toLocaleString()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <div className="small" style={{ textTransform: "none" as const, color: cardStyles.muted }}>
            Subscription details unavailable.
          </div>
        )}
      </section>

      <section
        style={{
          background: cardStyles.background,
          border: cardStyles.border,
          boxShadow: cardStyles.shadow,
          padding: "clamp(20px, 4vw, 28px)",
          display: "grid",
          gap: "12px",
        }}
      >
        <header className="mono" style={{ letterSpacing: "0.18em", textTransform: "uppercase" as const, color: cardStyles.heading }}>
          Usage
        </header>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ fontSize: "24px", fontWeight: 500 }}>{invoicesLast30}</div>
          <div className="small" style={{ textTransform: "none" as const, color: cardStyles.muted }}>
            invoices sent in the last 30 days
          </div>
        </div>
      </section>

      <section
        style={{
          background: cardStyles.background,
          border: cardStyles.border,
          boxShadow: cardStyles.shadow,
          padding: "clamp(20px, 4vw, 28px)",
          display: "grid",
          gap: "12px",
        }}
      >
        <header className="mono" style={{ letterSpacing: "0.18em", textTransform: "uppercase" as const, color: cardStyles.heading }}>
          Notification history
        </header>
        {notifications.length ? (
          <ul style={{ listStyle: "none", display: "grid", gap: "10px", padding: 0, margin: 0 }}>
            {notifications.map((entry, index) => (
              <li
                key={entry.id ?? `notification-${index}`}
                style={{
                  border: insetPanel.border,
                  padding: "12px",
                  display: "grid",
                  gap: "6px",
                  background: insetPanel.background,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                  <span className="mono" style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}>
                    {formatNotificationType(entry.type)}
                  </span>
                  <span className="mono small" style={{ letterSpacing: "0.08em", color: resolveStatusColor(entry.status ?? "recorded") }}>
                    {(entry.status ?? "recorded").toUpperCase()}
                  </span>
                </div>
                <div className="small" style={{ textTransform: "none" as const, color: cardStyles.muted }}>
                  {formatNotificationDetail(entry)}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="small" style={{ textTransform: "none" as const, color: cardStyles.muted }}>
            No subscription reminders or digests recorded yet.
          </div>
        )}
      </section>

      {checkoutOpen ? (
        <CheckoutDialog
          form={checkoutForm}
          contactOptions={contactOptions}
          cadenceOptions={cadenceOptions}
          pending={checkout.isPending}
          error={checkoutError}
          onChange={handleCheckoutFieldChange}
          onClose={closeCheckout}
          onSubmit={handleCheckoutSubmit}
          overlay={overlayTokens}
          outlineButton={outlineButton}
          primaryButton={primaryButton}
          input={inputTokens}
          headingColor={headingColor}
          mutedColor={mutedColor}
        />
      ) : null}
    </div>
  )
}

function StatusPanel({ children, panel }: { children: ReactNode; panel: PanelTone }) {
  return (
    <div
      style={{
        background: panel.background,
        border: panel.border,
        boxShadow: panel.shadow,
        padding: "clamp(20px, 4vw, 28px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 180,
        color: panel.heading,
      }}
      role="status"
      aria-live="polite"
    >
      <span className="small" style={{ textTransform: "none" as const, letterSpacing: "0.06em" }}>
        {children}
      </span>
    </div>
  )
}

function primaryButtonStyles(token: ButtonTone, disabled: boolean) {
  return {
    padding: "12px 18px",
    border: token.border,
    background: token.background,
    color: token.color,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    minHeight: "44px",
    boxShadow: token.shadow,
  }
}

function outlineButtonStyles(token: ButtonTone) {
  return {
    padding: "12px 18px",
    border: token.border,
    background: token.background,
    color: token.color,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    cursor: "pointer",
    minHeight: "44px",
    boxShadow: token.shadow,
  }
}

function outlineSmallButtonStyles(token: ButtonTone) {
  const base = outlineButtonStyles(token)
  return {
    ...base,
    padding: "6px 10px",
    minHeight: "32px",
    fontSize: "11px",
    letterSpacing: "0.1em",
  }
}

function formatPrice(amountCents: number | null, currency: string) {
  if (amountCents == null) return "—"
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amountCents / 100)
  } catch {
    return `${amountCents / 100} ${currency}`
  }
}

function resolveStatusColor(status: string) {
  const normalized = (status ?? "").toLowerCase()
  if (normalized === "active" || normalized === "enabled" || normalized === "recorded" || normalized === "sent")
    return "#10b981"
  if (normalized === "pending") return "#facc15"
  if (normalized === "canceling") return "#f97316"
  if (normalized === "canceled") return "#6b7280"
  return "#f97316"
}

function formatNotificationType(type: string | null | undefined) {
  if (!type) return "Notification"
  const normalized = type.toLowerCase()
  if (normalized === "renewal_reminder") return "Renewal reminder"
  if (normalized === "digest_summary") return "Digest summary"
  return type
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

function formatNotificationDetail(entry: SubscriptionNotificationEvent) {
  const parts: string[] = []
  if (entry.sent_at) {
    try {
      parts.push(`Sent ${new Date(entry.sent_at).toLocaleString()}`)
    } catch {
      parts.push(`Sent ${entry.sent_at}`)
    }
  }
  if (entry.channel) {
    parts.push(`via ${entry.channel.toUpperCase()}`)
  }
  const contact = entry.contact_name ?? entry.contact_email ?? entry.contact_id
  if (contact) {
    parts.push(`for ${contact}`)
  }
  return parts.join(" ")
}

function centsToDisplay(amountCents: number | null | undefined) {
  if (amountCents == null) return ""
  const value = amountCents / 100
  return Number.isInteger(value) ? value.toString() : value.toFixed(2)
}

function buildContactOptions(primary: string | null | undefined, records: SubscriptionRecord[]): ContactOption[] {
  const seen = new Set<string>()
  const options: ContactOption[] = []
  if (primary && primary.trim()) {
    const record = records.find((entry) => entry.contact_id === primary)
    options.push({ id: primary, label: record?.contact_name ?? primary })
    seen.add(primary)
  }
  for (const record of records) {
    const id = record.contact_id
    if (!id || seen.has(id)) continue
    options.push({ id, label: record.contact_name ?? id })
    seen.add(id)
  }
  return options
}

function SubscriptionRecordSection({
  title,
  records,
  defaultCurrency,
  defaultCadence,
  onOpenChat,
  onResumeCheckout,
  onAdjust,
}: {
  title: string
  records: SubscriptionRecord[]
  defaultCurrency: string
  defaultCadence: string
  onOpenChat: (record: SubscriptionRecord) => void
  onResumeCheckout: (record: SubscriptionRecord) => void
  onAdjust: (record: SubscriptionRecord) => void
}) {
  const surfaces = useThemeSurfaces()
  const panelTokens = surfaces.panel
  const insetPanel = panelTokens.inset
  const outlineButton = surfaces.button.outline
  const mutedColor = panelTokens.primary.muted

  if (!records.length) return null

  return (
    <section style={{ display: "grid", gap: "10px" }}>
      <span className="mono tiny" style={{ letterSpacing: "0.1em", textTransform: "uppercase", color: mutedColor }}>
        {title}
      </span>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "10px" }}>
        {records.map((record) => (
          <SubscriptionRecordItem
            key={record.id}
            record={record}
            defaultCurrency={defaultCurrency}
            defaultCadence={defaultCadence}
            onOpenChat={onOpenChat}
            onResumeCheckout={onResumeCheckout}
            onAdjust={onAdjust}
            mutedColor={mutedColor}
            insetPanel={insetPanel}
            outlineButton={outlineButton}
          />
        ))}
      </ul>
    </section>
  )
}

function SubscriptionRecordItem({
  record,
  defaultCurrency,
  defaultCadence,
  onOpenChat,
  onResumeCheckout,
  onAdjust,
  mutedColor,
  insetPanel,
  outlineButton,
}: {
  record: SubscriptionRecord
  defaultCurrency: string
  defaultCadence: string
  onOpenChat: (record: SubscriptionRecord) => void
  onResumeCheckout: (record: SubscriptionRecord) => void
  onAdjust: (record: SubscriptionRecord) => void
  mutedColor: string
  insetPanel: PanelInset
  outlineButton: ButtonTone
}) {
  const normalizedStatus = (record.status ?? "unknown").toLowerCase()
  const recordEmail = typeof record.metadata?.email === "string" ? (record.metadata.email as string) : null
  const showResume = normalizedStatus === "pending" && Boolean(record.checkout_url)

  return (
    <li
      data-testid={`subscription-record-${record.contact_id ?? record.id}`}
      style={{
        border: insetPanel.border,
        background: insetPanel.background,
        padding: "12px",
        display: "grid",
        gap: "6px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
        <span className="mono small" style={{ letterSpacing: "0.08em" }}>
          {record.contact_name ?? record.contact_id ?? "Contact"}
        </span>
        <span className="mono tiny" style={{ letterSpacing: "0.08em", color: resolveStatusColor(record.status ?? "") }}>
          {(record.status ?? "").toUpperCase() || "UNKNOWN"}
        </span>
      </div>
      <div className="small" style={{ color: mutedColor }}>
        {formatPrice(record.amount_cents ?? null, record.currency ?? defaultCurrency)} · {record.cadence ?? defaultCadence}
      </div>
      {recordEmail ? (
        <div className="tiny mono" style={{ color: mutedColor }}>Billing email {recordEmail}</div>
      ) : (
        <div className="tiny mono" style={{ color: "var(--warning-text)" }}>Add a billing email before sending customer checkout links.</div>
      )}
      <div className="tiny mono" style={{ color: mutedColor }}>
        Updated {record.updated_at ? new Date(record.updated_at).toLocaleString() : "—"}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <button type="button" className="tiny mono" style={outlineSmallButtonStyles(outlineButton)} onClick={() => onOpenChat(record)}>
          Open chat
        </button>
        {showResume ? (
          <button type="button" className="tiny mono" style={outlineSmallButtonStyles(outlineButton)} onClick={() => onResumeCheckout(record)}>
            Resume checkout
          </button>
        ) : null}
        <button type="button" className="tiny mono" style={outlineSmallButtonStyles(outlineButton)} onClick={() => onAdjust(record)}>
          Update plan
        </button>
      </div>
    </li>
  )
}

function CheckoutDialog({
  form,
  contactOptions,
  cadenceOptions,
  pending,
  error,
  onChange,
  onClose,
  onSubmit,
  overlay,
  outlineButton,
  primaryButton,
  input,
  headingColor,
  mutedColor,
}: {
  form: CheckoutFormState
  contactOptions: ContactOption[]
  cadenceOptions: string[]
  pending: boolean
  error: string | null
  onChange: (field: keyof CheckoutFormState, value: string) => void
  onClose: () => void
  onSubmit: () => void
  overlay: OverlayTokens
  outlineButton: ButtonTone
  primaryButton: ButtonTone
  input: ThemeSurfaces["input"]
  headingColor: string
  mutedColor: string
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: overlay.backdrop,
        display: "grid",
        placeItems: "center",
        zIndex: 20,
        padding: "16px",
      }}
    >
      <div
        style={{
          width: "min(480px, 96vw)",
          background: overlay.surface.background,
          border: overlay.surface.border,
          boxShadow: overlay.surface.shadow,
          padding: "24px",
          display: "grid",
          gap: "16px",
        }}
      >
        <header className="mono" style={{ letterSpacing: "0.14em", textTransform: "uppercase", color: headingColor }}>
          Launch checkout session
        </header>
        <label className="mono tiny" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }} htmlFor="billing-checkout-contact">
          Contact
        </label>
        <input
          id="billing-checkout-contact"
          list="billing-checkout-contact-options"
          value={form.contactId}
          onChange={(event) => onChange("contactId", event.target.value)}
          className="mono"
          style={{
            padding: "10px 12px",
            border: input.border,
            background: input.background,
            color: input.color,
            letterSpacing: "0.08em",
          }}
          placeholder="ct-123 or contact id"
        />
        <datalist id="billing-checkout-contact-options">
          {contactOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </datalist>
        <label className="mono tiny" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }} htmlFor="billing-checkout-email">
          Billing email
        </label>
        <input
          id="billing-checkout-email"
          type="email"
          value={form.email}
          onChange={(event) => onChange("email", event.target.value)}
          className="mono"
          style={{
            padding: "10px 12px",
            border: input.border,
            background: input.background,
            color: input.color,
            letterSpacing: "0.08em",
          }}
          placeholder="billing@example.com"
        />
        <span className="tiny mono" style={{ color: mutedColor, letterSpacing: "0.08em" }}>
          Required if the contact record is missing an email.
        </span>
        <div style={{ display: "grid", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <label className="mono tiny" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }} htmlFor="billing-checkout-amount">
              Amount ({form.currency || "USD"})
            </label>
            <input
              id="billing-checkout-amount"
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(event) => onChange("amount", event.target.value)}
              className="mono"
              style={{
                padding: "10px 12px",
                border: input.border,
                background: input.background,
                color: input.color,
                letterSpacing: "0.08em",
              }}
              placeholder="49.00"
            />
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <label className="mono tiny" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }} htmlFor="billing-checkout-cadence">
              Cadence
            </label>
            <select
              id="billing-checkout-cadence"
              value={form.cadence}
              onChange={(event) => onChange("cadence", event.target.value)}
              className="mono"
              style={{
                padding: "10px 12px",
                border: input.border,
                background: input.background,
                color: input.color,
                letterSpacing: "0.08em",
              }}
            >
              {cadenceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <label className="mono tiny" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }} htmlFor="billing-checkout-currency">
              Currency
            </label>
            <input
              id="billing-checkout-currency"
              value={form.currency}
              onChange={(event) => onChange("currency", event.target.value.toUpperCase())}
              className="mono"
              style={{
                padding: "10px 12px",
                border: input.border,
                background: input.background,
                color: input.color,
                letterSpacing: "0.08em",
                maxWidth: "120px",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <label className="mono tiny" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }} htmlFor="billing-checkout-description">
              Description
            </label>
            <input
              id="billing-checkout-description"
              value={form.description}
              onChange={(event) => onChange("description", event.target.value)}
              className="mono"
              style={{
                padding: "10px 12px",
                border: input.border,
                background: input.background,
                color: input.color,
                letterSpacing: "0.08em",
              }}
              placeholder="Monthly retainer"
            />
          </div>
        </div>
        {error ? (
          <div className="mono tiny" role="alert" style={{ color: "var(--error-text)" }}>
            {error}
          </div>
        ) : null}
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <button type="button" className="mono" onClick={onSubmit} disabled={pending} style={primaryButtonStyles(primaryButton, pending)}>
            Launch checkout
          </button>
          <button type="button" className="mono" onClick={onClose} disabled={pending} style={outlineButtonStyles(outlineButton)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export { SubscriptionRecordSection }
